<template>
  <div class="third-party-login" />
</template>

<script setup lang="ts">
import { useQuasar } from "quasar";

// Admin "Preview" handoff: the admin panel opens this page on the client origin
// with its auth-gateway access token in the URL FRAGMENT (#token=…), which browsers
// never send to the server (no access logs / Referer leaks). We drop that token into
// the `acc_token` cookie so the property page renders as the admin — including units
// that are not yet public. The backend accepts a gateway token only on the read-only
// preview routes (unit list/detail, unit reviews) and /users/me; every other
// investor route rejects it. See frontend-admin/composables/useUnitPreview.ts.

const FALLBACK_REDIRECT = "/";

const $q = useQuasar();
const route = useRoute();
const localePath = useLocalePath();

const rawRedirect = route.query.redirect;

// Read "#token=…" from the fragment (client-only page, ssr: false).
const readFragmentToken = (): string | null => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  return params.get("token");
};

// Only allow internal absolute paths ("/foo"), never protocol-relative ("//evil")
// or absolute URLs — guards against an open redirect.
const resolveRedirect = (value: unknown): string => {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//")) {
    return FALLBACK_REDIRECT;
  }
  return value;
};

onMounted(() => {
  const redirect = resolveRedirect(rawRedirect);
  const token = readFragmentToken();

  // Strip the token out of the address bar / history entry right away.
  window.history.replaceState(window.history.state, "", window.location.pathname);

  if (!token) {
    navigateTo(localePath(FALLBACK_REDIRECT));
    return;
  }

  $q.loading.show();

  // Carry over the admin session. Clear the refresh token: a gateway access token
  // cannot be refreshed via the native /auth/token/refresh endpoint, so on expiry
  // the admin simply re-opens the preview instead of swapping in a wrong session.
  useCookie("acc_token").value = token;
  useCookie("acc_refresh_token").value = null;

  navigateTo(localePath(redirect));
});

onBeforeUnmount(() => {
  $q.loading.hide();
});

definePageMeta({
  layout: false,
  ssr: false,
});
</script>

<style scoped lang="scss">
.third-party-login {
  width: 100dvw;
  height: 100dvh;
}
</style>
